/**
 * 정규식으로 데이터 유효성 검사
 * @param regExp 검사할 정규식
 * @param data 검사할 데이터
 * @returns 유효성 여부
 */
export const checkValid = (regExp: RegExp, data: any): boolean => regExp.test(data);

/**
 * 유효하지 않은 planHashValue인지 체크
 * @param phv planHashValue
 * @returns 유효하지 않으면 true
 */
export const isFalsyPhv = (phv: any): phv is undefined | null | -1 | '' => {
  return phv == null || phv === -1 || phv === '';
};

/**
 * 알림 값이 유효한 숫자인지 확인
 * @param value 검사할 값
 * @returns 0이 아닌 유효한 숫자일 때 true, 그 외에는 false
 */
export const isValidNumericAlertValue = (value: unknown): boolean => {
  const numericValue = Number(value);
  return !Number.isNaN(numericValue) && numericValue !== 0;
};

/**
 * 숫자 타입인지 확인
 * @param v 검사할 값
 * @returns 숫자 타입일 때 true, 그 외에는 false
 */
export const truthyNumber = (v: unknown): v is number => typeof v === 'number' && !Number.isNaN(v);

/**
 * 문자열 바이트 길이 반환
 * @param s 문자열
 * @returns 문자열 바이트 길이
 */
export const getStringByteLength = (s: string) => {
  const textEncoder = new TextEncoder();
  return textEncoder.encode(s).length;
};

export const isValidAddress = (address: string | number, ipVersion: 'IPv4' | 'IPv6' = 'IPv4') => {
  let isIpAddress = new RegExp(
    /^(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)$/,
  );
  const isDomainAddress = new RegExp(
    /^(?!-)(?!(?:.*--))(?:[a-zA-Z0-9가-힣](?:[a-zA-Z0-9가-힣-]{0,61}[a-zA-Z0-9가-힣])?\.)+(?:[a-zA-Z]{2,17}|xn--[a-zA-Z0-9]{2,17})$/,
  );

  if (ipVersion === 'IPv6') {
    isIpAddress = new RegExp(
      /(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))/,
    );
  }

  return isIpAddress.test(address.toString()) || isDomainAddress.test(address.toString());
};

export const isValidPort = (port: string | number) => {
  const num = +port;
  const isPort = new RegExp(/^(0|([1-9][0-9]{0,3})|([1-5][0-9]{4})|6[0-5][0-5][0-3][0-5])$/);

  return Number.isInteger(num) && num >= 0 && num <= 65535 && isPort.test(port.toString());
};

export const isValidName = (str: string) => {
  const isName = new RegExp(/^[a-zA-Z0-9ㄱ-ㅎㅏ-ㅣ가-힣-_]+$/);

  return isName.test(str);
};

export const isValidUrl = (url: string) => {
  const urlRegex = new RegExp(
    /^(https?:\/\/)([a-zA-Z0-9가-힣-]+\.)+[a-zA-Z]{2,}|localhost(:\d{1,5})?(\/[a-zA-Z0-9가-힣-._~:/?#[\]@!$&'()*+,;=]*)?$|^(https?:\/\/)(\d{1,3}\.){3}\d{1,3}(:\d{1,5})?(\/[a-zA-Z0-9가-힣-._~:/?#[\]@!$&'()*+,;=]*)?$/,
  );
  return urlRegex.test(url);
};

// CIDR || IP Address validation
export const isValidCidrIp = (cidrIp: string) => {
  const cidrRegex =
    /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\/(3[0-2]|[12]?[0-9]))$/;

  const isIpAddress = new RegExp(
    /^(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)$/,
  );
  return cidrRegex.test(cidrIp.toString()) || isIpAddress.test(cidrIp.toString());
};

export const isAsciiString = (str: string) => {
  // eslint-disable-next-line no-control-regex
  return typeof str === 'string' && /^[\x00-\x7F]*$/.test(str);
};

export const isValidJson = (jsonString: string) => {
  if (typeof jsonString !== 'string') return false;

  try {
    const parsed = JSON.parse(jsonString);
    return typeof parsed === 'object' && parsed !== null;
  } catch {
    return false;
  }
};

export const isEmptyOrNil = (value) => {
  return value === null || value === undefined || value === '';
};
